import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { pushSupported, isPushSubscribed, subscribePush } from '../utils/pushSubscribe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

export function HampusReminderSettings() {
  const [r, setR] = useState(null);
  const [savedTime, setSavedTime] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/hp-daily/reminder`, { withCredentials: true }).then((res) => { setR(res.data); setSavedTime(res.data.time); }).catch(() => {});
    isPushSubscribed().then(setSubscribed).catch(() => {});
  }, []);

  const save = async (next) => {
    setR(next);
    try {
      await axios.put(`${API}/hp-daily/reminder`, next, { withCredentials: true });
      setSavedTime(next.time);
      toast.success(next.enabled ? `🐹 Hampus påminner dig ${next.time}` : 'Påminnelsen är pausad');
    } catch { toast.error('Kunde inte spara'); }
  };
  const enablePush = async () => {
    try { await subscribePush(); setSubscribed(true); toast.success('🔔 Notiser på! Hampus kan nu nå dig'); }
    catch (e) { toast.error(e?.message || 'Kunde inte aktivera notiser'); }
  };

  if (!r) return null;
  return (
    <section className="neo-card p-4 bg-[#FFF6D6] space-y-2" data-testid="hp-reminder-settings">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-['Fredoka'] font-bold text-lg">🔔 Hampus påminnelse</h4>
        <button onClick={() => save({ ...r, enabled: !r.enabled })} data-testid="hp-reminder-toggle"
          className={`w-12 h-7 rounded-full border-2 border-black relative transition-colors ${r.enabled ? 'bg-[#B9FBC0]' : 'bg-white'}`}>
          <span className={`absolute left-0 top-0.5 w-5 h-5 rounded-full bg-black transition-transform ${r.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>
      <p className="text-xs opacity-70">Hampus hör av sig bara om dagens pass inte är gjort.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="time" value={r.time} onChange={(e) => setR({ ...r, time: e.target.value })} onBlur={() => { if (r.time !== savedTime) save(r); }}
          className="neo-input text-sm px-2 py-1" data-testid="hp-reminder-time" />
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => save({ ...r, days: r.days.includes(i) ? r.days.filter((x) => x !== i) : [...r.days, i] })}
            className={`text-xs font-bold px-2 py-1 rounded-full border-2 border-black transition-colors ${r.days.includes(i) ? 'bg-[#F5B841]' : 'bg-white opacity-60'}`}
            data-testid={`hp-reminder-day-${i}`}>{d}</button>
        ))}
      </div>
      {pushSupported() && !subscribed && (
        <button onClick={enablePush} className="neo-btn px-3 py-1.5 bg-white text-xs font-bold" data-testid="hp-reminder-enable-push">
          Aktivera notiser på den här enheten
        </button>
      )}
      {subscribed && <p className="text-[11px] opacity-60" data-testid="hp-reminder-push-on">✓ Notiser är på för den här enheten</p>}
    </section>
  );
}

export default function HampusNudge() {
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    const check = () => axios.get(`${API}/hp-daily/nudge`, { withCredentials: true })
      .then((r) => setNudge(r.data?.show ? r.data : null)).catch(() => {});
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    const hide = () => setNudge(null);
    window.addEventListener('hampus:pass-done', hide);
    return () => { clearInterval(t); window.removeEventListener('hampus:pass-done', hide); };
  }, []);

  if (!nudge) return null;
  const open = () => {
    try { sessionStorage.setItem('minnes-subtab', 'hp'); } catch {}
    window.dispatchEvent(new CustomEvent('focus-critter:navigate', { detail: { tab: 'minnes' } }));
    setNudge(null);
  };
  const dismiss = () => {
    axios.post(`${API}/hp-daily/nudge/dismiss`, {}, { withCredentials: true }).catch(() => {});
    setNudge(null);
  };

  return (
    <div className="fixed bottom-24 left-4 z-[90] max-w-xs rounded-2xl border-[3px] border-black bg-[#FFF6D6] shadow-[4px_4px_0_#000] p-3 flex items-start gap-2" data-testid="hampus-nudge">
      <span className="text-3xl">🐹</span>
      <button onClick={open} className="flex-1 text-left" data-testid="hampus-nudge-open">
        <div className="text-[10px] font-black uppercase tracking-widest opacity-60">Hampus · dag {nudge.day_number}</div>
        <div className="text-sm font-bold leading-tight">Dagens HP-pass väntar: {nudge.part.emoji} {nudge.part.name}</div>
        <div className="text-[11px] opacity-70">5 små frågor, ingen stress 💛</div>
      </button>
      <button onClick={dismiss} className="p-1 opacity-60 hover:opacity-100" data-testid="hampus-nudge-dismiss" aria-label="Stäng"><X className="w-4 h-4" /></button>
    </div>
  );
}
